/**
 * Contact Form Component
 *
 * Handles client-side validation and AJAX submission for contact forms.
 *
 * @package WPSCT
 */
export class ContactForm {
  constructor() {
    this.forms = document.querySelectorAll(
      ".wpsct-contact-form, .wpsct-contact-main-form",
    );

    if (!this.forms.length) {
      return;
    }

    this.forms.forEach((form) => this.initForm(form));
  }

  initForm(form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSubmit(form);
    });

    form.querySelectorAll("input, textarea").forEach((field) => {
      field.addEventListener("input", () => {
        this.clearFieldError(form, field.name);
      });
    });
  }

  isMainForm(form) {
    return form.classList.contains("wpsct-contact-main-form");
  }

  async handleSubmit(form) {
    this.hideMessage(form);
    this.clearAllErrors(form);

    const errors = this.validate(form);
    if (Object.keys(errors).length > 0) {
      this.showFieldErrors(form, errors);
      return;
    }

    if (
      typeof turnstile !== "undefined" &&
      form.querySelector(".cf-turnstile")
    ) {
      const turnstileWidget = form.querySelector(".cf-turnstile");
      const widgetId = turnstileWidget.dataset.widgetId;
      const turnstileResponse = widgetId
        ? turnstile.getResponse(widgetId)
        : turnstile.getResponse();

      if (!turnstileResponse) {
        const errorEl = form.querySelector(".form__turnstile .form__error");
        if (errorEl) {
          errorEl.textContent = "Please complete the verification.";
          errorEl.classList.add("is-visible");
        }
        return;
      }
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const textEl = submitBtn?.querySelector(".text");
    const originalText = textEl ? textEl.textContent : submitBtn?.textContent;

    if (submitBtn) {
      submitBtn.disabled = true;
    }
    if (textEl) {
      textEl.textContent = "SENDING...";
    } else if (submitBtn) {
      submitBtn.textContent = "SENDING...";
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(wpsTheme.ajaxUrl, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        this.showMessage(form, "success");
        form.reset();
      } else {
        if (result.data?.errors) {
          this.showFieldErrors(form, result.data.errors);
        }
        this.showMessage(form, "error");
      }
    } catch {
      this.showMessage(form, "error");
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
      }
      if (textEl) {
        textEl.textContent = originalText;
      } else if (submitBtn) {
        submitBtn.textContent = originalText;
      }

      if (
        typeof turnstile !== "undefined" &&
        form.querySelector(".cf-turnstile")
      ) {
        const turnstileWidget = form.querySelector(".cf-turnstile");
        const widgetId = turnstileWidget.dataset.widgetId;
        if (widgetId) {
          turnstile.reset(widgetId);
        } else {
          turnstile.reset();
        }
      }
    }
  }

  validate(form) {
    if (this.isMainForm(form)) {
      return this.validateMainForm(form);
    }
    return this.validateLegacyForm(form);
  }

  validateMainForm(form) {
    const errors = {};
    const firstName = form.querySelector('[name="contact_first_name"]');
    const lastName = form.querySelector('[name="contact_last_name"]');

    if (!firstName?.value.trim()) {
      errors.contact_first_name = "First name is required.";
    }

    if (!lastName?.value.trim()) {
      errors.contact_last_name = "Last name is required.";
    }

    return errors;
  }

  validateLegacyForm(form) {
    const errors = {};
    const name = form.querySelector('[name="contact_name"]');
    const email = form.querySelector('[name="contact_email"]');

    if (!name?.value.trim()) {
      errors.contact_name = "Name is required.";
    }

    if (!email?.value.trim()) {
      errors.contact_email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
      errors.contact_email = "Please enter a valid email address.";
    }

    return errors;
  }

  showFieldErrors(form, errors) {
    Object.keys(errors).forEach((fieldName) => {
      const field = form.querySelector(`[name="${fieldName}"]`);
      if (!field) {
        return;
      }

      field.classList.add("has-error");

      const errorEl = this.isMainForm(form)
        ? form.querySelector(`[data-error-for="${fieldName}"]`)
        : field.closest(".form__field")?.querySelector(".form__error");

      if (errorEl) {
        errorEl.textContent = errors[fieldName];
        errorEl.classList.add("is-visible");
      }
    });
  }

  clearFieldError(form, fieldName) {
    const field = form.querySelector(`[name="${fieldName}"]`);
    if (!field) {
      return;
    }

    field.classList.remove("has-error");

    const errorEl = this.isMainForm(form)
      ? form.querySelector(`[data-error-for="${fieldName}"]`)
      : field.closest(".form__field")?.querySelector(".form__error");

    if (errorEl) {
      errorEl.textContent = "";
      errorEl.classList.remove("is-visible");
    }
  }

  clearAllErrors(form) {
    form.querySelectorAll(".has-error").forEach((el) => {
      el.classList.remove("has-error");
    });

    form.querySelectorAll(".form__error.is-visible").forEach((el) => {
      el.textContent = "";
      el.classList.remove("is-visible");
    });
  }

  showMessage(form, type) {
    const messageEl = form.querySelector(".form__message");
    if (!messageEl) {
      return;
    }

    const content =
      type === "success" ? messageEl.dataset.success : messageEl.dataset.error;

    if (this.isMainForm(form)) {
      messageEl.textContent = content || "";
    } else {
      messageEl.innerHTML = content || "";
    }

    messageEl.className = `form__message is-visible is-${type}`;
  }

  hideMessage(form) {
    const messageEl = form.querySelector(".form__message");
    if (!messageEl) {
      return;
    }

    messageEl.textContent = "";
    messageEl.innerHTML = "";
    messageEl.className = "form__message";
  }
}
